/**
 * `PlanDraft` → the backend's POST / PATCH body. **The inverse of `responseToDraft`, and the half
 * that can destroy data.**
 *
 * The save is whole-tree replace (document-model ADR §2): `Phases` is PUT-style, so this function's
 * output IS the plan after the save. A field it fails to emit is a field the trainer loses, and the
 * loss shows up on the NEXT load rather than here — which is why the read half was built first, in
 * its own pull request, and why the marquee test over the committed fixture is a full round trip
 * rather than a per-field assertion.
 *
 * POST and PATCH share the SAME nested `Post*Input` tree and differ only at the top level: POST
 * requires `name` / `category` / `schedulingMode` / `phases`, PATCH makes every top-level field
 * independently optional and adds `baseVersion`.
 *
 * ── A PATCH CANNOT CLEAR A TOP-LEVEL STRING, and this mapper cannot change that ───────────────
 * `PlanTemplateProcessor.UpdateAsync` applies each optional top-level field behind an
 * `is not null` guard — `if (request.Description is not null) entity.Description = request.Description;`
 * and the identical line for the cover (`CoverId` since 14 August 2026, `CoverImageKey` before it).
 * C# cannot tell an ABSENT JSON member from an explicit
 * `null`: both deserialise to `null` on a nullable property, so both mean "leave it alone".
 *
 * The consequence is a real, currently unreachable gap rather than a mapper bug: a trainer who
 * empties the plan's description and saves gets a 200 and keeps the old description, because
 * `trimToNull` sends `null` and the backend reads that as no instruction. Nothing in PR-B1 edits
 * either field, so nobody can hit it yet. The fix is the backend's — an explicit sentinel, or
 * `JsonElement`-based presence detection — and it is NOT worked around here: emitting `""` to force
 * a clear would store an empty string where the column means "no description", and the two clients
 * would then disagree about what an unset description looks like.
 *
 * ── Ported from `move-with-me-frontend/.../plans/builder/model/draftToRequest.ts` ─────────────
 * Near-verbatim, per execution ADR §1: "replicate the mobile flow verbatim … every place this app
 * invents behaviour is a place the two clients can disagree about the same plan." The tree-shape
 * decisions came with it and are load-bearing:
 *
 * · **ONE builder day = ONE backend `SessionTemplate`.** The day's `durationMinutes` becomes that
 *   session's `estimatedDurationMinutes` and its `restSeconds` the `defaultRestTimeSeconds`.
 * · **`order` is the 1-based ARRAY POSITION at every level.** This is not a renumber of anything the
 *   draft holds — the draft carries no `order` field on a day or an exercise at all, only the
 *   `serverOrder` it was loaded with, which is captured for the recalc preview and NEVER emitted.
 *   Position is safe to derive precisely because position no longer carries identity: `slotToken`
 *   does for days and exercises, the server row `Id` does for phases. Sessions still reconcile
 *   positionally, which is the known backend limit behind the multi-session refusal.
 * · **`PostSetInput` has no index field.** Sets are ordered by array position, and `setType`
 *   defaults to `Working` when the draft leaves it null.
 * · **An exercise-less day is DROPPED rather than serialised.** `PostSessionTemplateInput.Exercises`
 *   is `[MinLength(1)]` and the backend does not tolerate a null list, so such a day is a
 *   structurally invalid body. There is no save gate above this — `phaseToInput` IS the guard, and
 *   the phase that drops its last day REFUSES rather than emitting a day-less phase (see
 *   `PhaseSerialisationError`). An earlier revision of this comment called the gate "the primary
 *   guard" and this one "defence in depth"; no such gate was ever built, and the correction is
 *   recorded rather than quietly applied because "something upstream checks this" is exactly the
 *   belief that lets a silent data loss through.
 *
 * ── The seven places this port diverges, each with its reason ─────────────────────────────────
 * The list is exhaustive on purpose: it is what a reader diffs the two files against, and an
 * incomplete one asserts that everything unlisted is identical. It said "six" while listing five
 * until 2026-08-02; the missing entry is divergence 6, and it was missing because the behaviour it
 * describes was missing too.
 *
 * 1. **`prescribedTempo` and `prescribedSpeed` are EMITTED.** Mobile's `setToInput` omits both, so
 *    every save it makes wipes them — the write-side half of the read-side defect recorded in
 *    `planDraft.types.ts` note 5. Copying mobile here is what would destroy the plan, so this is the
 *    one place the port deliberately diverges toward not losing data. Neither is truncated or
 *    clamped: see `TEMPO_MAX_LENGTH` in `planCaps.ts` for who owns the length.
 *
 *    **The cost of that choice, stated so it is a known edge rather than a surprise 400.** The
 *    backend judges every prescribed value against the exercise's own measurement contract:
 *    `MeasurementContract.Objection` answers `NotMeasured` for a value on a field the exercise does
 *    not measure, and `PlanTemplateProcessor` refuses the save on it. So if an exercise's
 *    measurement fields stop including tempo or speed AFTER a plan was authored with one — an admin
 *    edit to the library row, a contract migration — this app re-sends the stored value and the
 *    trainer gets a 400 on a plan they did not change. Mobile never hits it, because it drops both
 *    fields unconditionally and thereby drops the stale value too.
 *
 *    Not worked around here, deliberately: filtering a set's values against `measurementFields` on
 *    the way out is the same "helpful" silent drop that makes mobile clear a tempo on every save,
 *    and it would delete the trainer's authored notation to avoid an error message about it. A 400
 *    naming the field is the honest outcome, and the fix belongs where the contract changed.
 * 2. **The per-set `note` is DROPPED.** Gap G4 (execution ADR §1): `Set` has no note column, so
 *    there is nowhere for the value to land.
 *
 *    The reason stated here until 2026-08-02 — "the strict parser 400s on an unknown field" — is
 *    FALSE, and the correction matters because it was the stated justification for a behaviour that
 *    is nonetheless right. `Program.cs` configures MVC's JSON with a string-enum converter and
 *    nothing else; `JsonUnmappedMemberHandling.Disallow` appears exactly once in the backend, on
 *    `ProgressionRuleJson.Options`, which is private to the progression-rule payload. An unknown
 *    member on `PostSetInput` is therefore SILENTLY IGNORED by `System.Text.Json`, not rejected. The
 *    note is still dropped — a field the database cannot store is not preserved by sending it — but
 *    nobody should expect a 400 to catch the next unknown field this mapper emits.
 * 3. **`category` is carried VERBATIM, and omitted from a PATCH when the draft has none.** Mobile
 *    normalises to an internal key and falls back to `'strength'`, which is safe there because its
 *    builder always renders a category picker. Here a whole-tree save would PERSIST that fallback,
 *    changing the trainer's plan as a side effect of opening it. PATCH's top-level fields are
 *    independently optional (ADR §2), so omitting one means "leave it alone" — which is exactly the
 *    right reading of a plan the server stored with no category. POST has no such option: `category`
 *    is required there, and a brand-new draft always has one (`draftFactory`).
 *
 *    `schedulingMode` is NOT treated the same way, and the asymmetry is deliberate. The draft
 *    always HAS a mode — `readPlanDocument` refuses a plan whose wire mode has no canvas here, and
 *    the create dialog picks one for every new plan — so "the draft has none" is not a state that
 *    exists. Re-emitting it is also what mobile does, which keeps the two clients' PATCH bodies
 *    comparable. A mode-CHANGING PATCH on a live fork is refused with `plan_mode_locked`, which is
 *    R1 enforced at the wire; echoing the mode the read supplied is not a change, and nothing in
 *    this app can change it.
 * 4. **No `dropIds` parameter.** Mobile's served its `?sourceTemplateId=` prefill-as-new flow by
 *    dropping ids on the way OUT. This app copies a template on the way IN instead
 *    (`draftCopy.ts`, 23 September 2026): the copy carries no server identity from the moment it
 *    exists, so the builder, the assign preview and this serialiser all see the same new plan, and
 *    no body anywhere depends on remembering to drop something.
 * 5. **The default session name is not a hard-coded Polish word.** Mobile falls back to `'Trening'`.
 *    `PostSessionTemplateInput.Name` is `[MinLength(1)]`, so SOMETHING must go there — and this app
 *    has the day's own session name, which mobile's draft does not carry (`DayDraft.sessionName`,
 *    port note 6). The chain is session name → day name → the constant, so the literal is reached
 *    only by a day that has neither, and it is DATA labelling a wire row rather than copy anyone
 *    reads. It is not i18n and must not become i18n: `model/` takes no `t`, and a wire value that
 *    changed with the trainer's interface language would make one plan two.
 * 6. **The SESSION note is emitted.** Mobile's draft carries no session-level note at all, so its
 *    `dayToSessionInput` sends none — and because `Phases` is PUT-style, every save mobile makes
 *    would clear a note some other client wrote. It never bites there because mobile is the only
 *    client that writes one. It bit here immediately: this app read the session but not its note, so
 *    renaming one workout in the browser wiped `SessionTemplate.Notes` off every session in the
 *    plan, and the trainer would have found out on the phone rather than here. `DayDraft.sessionNotes`
 *    is the field, and it is preserved with no editor above it — an editor arrives in PR-B2.
 * 7. **The progression rule is carried OPAQUELY, and there is no rule gate.** This entry replaces
 *    the one that stood here until 15 August 2026, which said "every exercise's progression rule is
 *    re-validated here" and described a `storedRuleRefusal` call in `exerciseToInput`.
 *
 *    **That gate is REMOVED, and removing it is the safe direction.** It validated
 *    `ExerciseDraft`'s flat progression fields, which since the contract change are dormant load-time
 *    DEFAULTS rather than a reading of the stored rule (`progressionRuleWire.ts` is frozen). A guard
 *    asking a question about a value nobody authored answers about nothing — and the honest failure
 *    mode is the dangerous one: it would refuse saves of plans the trainer never touched, on the
 *    evidence of defaults this mapper invented. `DraftSerialisationError`'s `ruleRefused` shape, the
 *    refusal module and its i18n key all stay, unreached, for the progression port to re-wire.
 *
 *    What ships instead is `progressionRules`, byte-identical to what arrived: read once by
 *    `responseToDraft`, held untouched, spread back on. An exercise that came with no rule emits no
 *    key. The precedent is `baseVersion` and the contract is on `ExerciseDraft.progressionRules`.
 *
 * ── The freeform lifespan, and which omissions ADD data (15 August 2026) ─────────────────────
 * The hazard this file's header opens with — an unemitted field is a lost field — has a sharper
 * form on the day and exercise cycle windows, and it points the other way. The backend reads an
 * absent `activationCycle` / `retirementCycle` as "the whole host" and an absent `excludedCycles` as
 * "no holes", so forgetting one does not narrow the trainer's plan, it WIDENS it: the client is
 * minted the workouts the trainer removed. All three ship on every day, and the exercise's two ship
 * on every exercise, whether or not any surface authors them yet. Exercise `activationCycle` is the
 * one part that stays absent, because the contract has no field for it.
 *
 * No React, no DOM, no query hook, no i18next: this is `model/`.
 */

import type {
  TrainWithMeModulesPlansDtosRequestsPatchPlanTemplateRequest,
  TrainWithMeModulesPlansDtosRequestsPostDayTemplateInput,
  TrainWithMeModulesPlansDtosRequestsPostExerciseTemplateInput,
  TrainWithMeModulesPlansDtosRequestsPostPhaseTemplateInput,
  TrainWithMeModulesPlansDtosRequestsPostPlanTemplateRequest,
  TrainWithMeModulesPlansDtosRequestsPostSessionTemplateInput,
  TrainWithMeModulesPlansDtosRequestsPostSetInput,
} from '~/shared/api/generated/model';

import type { MeasurementFieldKey } from '~/shared/measurement';
import type { DayDraft, ExerciseDraft, PhaseDraft, PlanDraft, SetDraft } from './planDraft.types';
import { phaseTypeToApi, schedulingModeToApi } from './planEnums';
import type { RuleRefusal } from './progressionRefusals';
import { overrideWireInputs } from './progressionRuleWire';
import { rulesToWire } from './progressionWire';
import { amrapShippedCaps } from './setShape';

type PostRequest = TrainWithMeModulesPlansDtosRequestsPostPlanTemplateRequest;
type PatchRequest = TrainWithMeModulesPlansDtosRequestsPatchPlanTemplateRequest;
type PhaseInput = TrainWithMeModulesPlansDtosRequestsPostPhaseTemplateInput;
type DayInput = TrainWithMeModulesPlansDtosRequestsPostDayTemplateInput;
type SessionInput = TrainWithMeModulesPlansDtosRequestsPostSessionTemplateInput;
type ExerciseInput = TrainWithMeModulesPlansDtosRequestsPostExerciseTemplateInput;
type SetInput = TrainWithMeModulesPlansDtosRequestsPostSetInput;

/**
 * The last-resort session name. DATA labelling a wire row, never copy — see divergence 6. Reached
 * only by a day carrying neither a session name nor a day name.
 */
const FALLBACK_SESSION_NAME = 'Trening';

/** The lifecycle kind a set ships as when the draft leaves `setType` null. */
const DEFAULT_SET_TYPE = 'Working';

/**
 * The category a brand-new plan ships when nothing has set one. Only ever reached on POST, where the
 * wire requires a value — and `draftFactory` already supplies one, so this is the shape of "required
 * field, no answer" rather than a policy. The backend's own default enum member, verbatim.
 */
const FALLBACK_CATEGORY = 'Strength';

/** Why a draft refused to serialise. Each one is a different thing to tell the trainer. */
export type DraftSerialisationReason = 'emptyPhase' | 'unidentifiedExercise' | 'ruleRefused';

/**
 * A draft that cannot be serialised without losing or corrupting the trainer's plan. **Thrown, never
 * returned as a body** — the entire point is that no request leaves.
 *
 * Three shapes qualify, and all three are places where the previous code produced a body the backend
 * would have accepted, or half-accepted, while meaning something other than what the trainer did:
 *
 * · `emptyPhase` — a phase whose every day lost its last exercise. `PostPhaseTemplateInput.Days` is
 *   `[MinLength(1)]`, so the honest options are a 400 or a refusal from here. What is NOT an option
 *   is what this mapper used to do: emit `days: undefined`, which serialises to an ABSENT key, and
 *   PATCH reads an absent collection as "leave it alone" — so the phase would have been saved with
 *   the days it had BEFORE the trainer emptied it, silently, with a 200 and a green save state.
 * · `unidentifiedExercise` — an exercise with no real library id. `responseToDraft` floors a missing
 *   `exerciseId` to `0` because the generated DTO types it non-nullable; `0` is not a row, and the
 *   backend answers the whole save with `Exercises not found: 0`. Refusing here names the exercise
 *   the trainer would otherwise have to find by reading a numeric id out of an error toast.
 * · `ruleRefused` — an exercise whose stored progression rule no longer stands against its own sets.
 *   **The rule bar is not enough on its own**, and that is the whole reason this exists: the bar
 *   refuses a rule the sets cannot carry, but nothing re-asks once the rule is applied. Delete the
 *   AMRAP set under an inbound amrap rule, or clear the last prescribed weight under a Static kilo-
 *   gram delta, and the tree becomes unsaveable on a screen that never mentioned the rule. Before
 *   this, that arrived as a code-less 400 and `saveErrorCopy` had nothing to match on, so the
 *   trainer read "nie udało się zapisać planu. Sprawdź połączenie" about a connection that was fine.
 *   The refusal travels with the error so the save path can word it — see `refusal` below.
 *
 * `emptyPhase` and `unidentifiedExercise` were guards written ahead of the surfaces that can reach
 * them. `ruleRefused` is reachable today, from the set table.
 */
export class DraftSerialisationError extends Error {
  readonly reason: DraftSerialisationReason;
  /** The phase or exercise name, so a caller can say WHICH without re-walking the tree. */
  readonly nodeName: string;
  /**
   * The rule refusal, in the same i18n key the rule bar shows for it. Present on `ruleRefused` and
   * absent on the other two, which have nothing further to say than their own copy.
   */
  readonly refusal: RuleRefusal | null;

  constructor(
    reason: DraftSerialisationReason,
    nodeName: string,
    detail: string,
    refusal: RuleRefusal | null = null,
  ) {
    super(detail);
    this.name = 'DraftSerialisationError';
    this.reason = reason;
    this.nodeName = nodeName;
    this.refusal = refusal;
  }
}

/** Trim, and collapse a whitespace-only string to `null` — one representation of "nothing here". */
function trimToNull(value: string | null): string | null {
  if (value === null) return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/**
 * Serialise one set. **Every authored value ships**, because values ARE the column state: a column
 * the trainer removed has already had its values cleared across the sets, so there is nothing left
 * for a per-column wire filter to do except become a second place for the two to disagree.
 *
 * The one exception is the AMRAP cap pair, which is a different rule and belongs at the wire — see
 * `amrapShippedCaps`. The per-set note is dropped here and nowhere else (divergence 2).
 */
function setToInput(set: SetDraft, measurementFields: readonly MeasurementFieldKey[]): SetInput {
  const caps = amrapShippedCaps(set, measurementFields);
  return {
    setType: set.setType ?? DEFAULT_SET_TYPE,
    prescribedWeight: set.prescribedWeight,
    prescribedReps: set.prescribedReps,
    prescribedRpe: caps.prescribedRpe,
    prescribedRir: caps.prescribedRir,
    prescribedRestTime: set.prescribedRestTime,
    prescribedDurationSeconds: set.prescribedDurationSeconds,
    prescribedDistanceMeters: set.prescribedDistanceMeters,
    prescribedRounds: set.prescribedRounds,
    prescribedPercent: set.prescribedPercent,
    // Divergence 1: mobile emits neither, and therefore clears both on every save it makes.
    prescribedTempo: set.prescribedTempo,
    prescribedSpeed: set.prescribedSpeed,
    // The `PreviousExecution` ceiling. Nothing here authors one; it is emitted because the set row is
    // REPLACED by this body, so omitting a stored ceiling deletes it.
    prescribedCeiling: set.prescribedCeiling,
    // `set.note` is DELIBERATELY absent. Divergence 2 — gap G4.
  };
}

/**
 * What a serialisation is FOR. A plan save resolves every exercise by its library id, so a slot with
 * none is refused here. An edit of Buddy's draft (`draftToDraftEditRequest`) is the one request that
 * keeps such a slot: the draft holds exercises the trainer has not decided yet (ai-plan-builder R8),
 * the wire says `exerciseId: 0` for each, and the draft-edit route expects them back exactly so.
 */
type UnidentifiedExercises = 'refuse' | 'keep';

function exerciseToInput(exercise: ExerciseDraft, index: number, unidentified: UnidentifiedExercises): ExerciseInput {
  // `0` is the floor `responseToDraft` writes when the wire carried no id, and it is not a library
  // row: the backend refuses the WHOLE save with `Exercises not found: 0`, naming a number the
  // trainer cannot act on. Refuse here instead, where the exercise still has a name.
  const identified = Number.isInteger(exercise.exerciseId) && exercise.exerciseId > 0;
  if (!identified && !(unidentified === 'keep' && exercise.exerciseId === 0)) {
    throw new DraftSerialisationError(
      'unidentifiedExercise',
      exercise.name,
      `Exercise "${exercise.name}" carries no library id (${String(exercise.exerciseId)}), so it ` +
        `cannot be serialised: the backend resolves every exercise by id and would refuse the ` +
        `whole plan rather than this one row.`,
    );
  }
  // The stored-rule gate that used to stand here is GONE, deliberately — see divergence 7.
  return {
    exerciseId: exercise.exerciseId,
    // The slot's durable identity, echoed VERBATIM. This is what keeps the exercise's activation
    // cycle and its progression chain across a save; a fresh one here would read as a first
    // exposure and silently restart the client's progression.
    slotToken: exercise.slotToken,
    order: index + 1,
    role: exercise.role,
    group: trimToNull(exercise.group),
    // The coaching tip MUST ship on every save: a PATCH replaces the whole phase tree, so omitting
    // it deleted whatever tip the server held. That was a real defect on mobile — every save from
    // the app silently wiped it.
    notes: trimToNull(exercise.notes),
    // The rule map, back out. `rulesToWire` is TOTAL — every entry the draft holds ships, including
    // rules on columns no surface in this build can show — so a load, an unrelated edit and a save
    // leave a richer document intact. An exercise with no rule emits an EMPTY map rather than none
    // at all, which the backend reads as its flat default; that is the one spelling of "nothing
    // progresses here", and `null` would be a second for the two clients to disagree about.
    progressionRules: rulesToWire({
      rules: exercise.progressionRules,
      unreadable: exercise.unreadableProgressionRules,
    }),
    // The RealMax pair, now fields of the exercise rather than of a rule document. Emitted verbatim.
    realMaxMultiplier: exercise.realMaxMultiplier,
    realMaxSource: exercise.realMaxSource,
    // The exercise's cycle lifespan. `retirementCycle` MUST ship: the backend reads an absent end as
    // "the whole host", so omitting a stored one widens the exercise over every remaining cycle of
    // its phase — an omission here ADDS prescription rather than losing it. `excludedCycles` is
    // always a real array for the same reason `setOverrides` is: an omitted list is a cleared list,
    // and clearing it puts the exercise back into the cycles it was sitting out. Copied rather than
    // passed through, so a request body can never alias the draft's own array.
    retirementCycle: exercise.retirementCycle,
    excludedCycles: [...exercise.excludedCycles],
    prescriptionMode: exercise.prescriptionMode,
    sets: exercise.sets.map((set) => setToInput(set, exercise.measurementFields)),
    // ALWAYS an array, empty included. The request is the only source of manual overrides — the
    // server carries nothing across a save — so this list IS the exercise's override set, and an
    // omitted cell is cleared.
    setOverrides: overrideWireInputs(exercise),
    // `activationCycle` is DELIBERATELY absent, and it is the ONE half of the lifespan that stays so:
    // an exercise's activation is server-resolved per slot token (`ExerciseActivationPlan`) and
    // `PostExerciseTemplateInput` has no field for it. Its DAY's activation is a different question
    // and IS emitted — see `dayToInput`. `serverCycles` is absent for the other reason: it is the
    // server's resolution of this window, so sending it back would be sending the server its own
    // answer as an instruction. Neither is the row `id` emitted — the backend rebuilds the tree from
    // position and identity, never from row ids.
  };
}

/**
 * One exercise in the save request's shape, as the edit of Buddy's draft sends it — the request the
 * AI's fingerprint of an exercise is taken over (`aiFingerprint.ts`). Its `order` is a placeholder:
 * a position belongs to the day around the exercise, and the fingerprint leaves it out.
 */
export function exerciseSaveShape(exercise: ExerciseDraft): ExerciseInput {
  return exerciseToInput(exercise, 0, 'keep');
}

/**
 * A builder day → its single `SessionTemplate` input.
 *
 * The exercises array is ALWAYS a real array, never collapsed to `undefined`: the wire's `Exercises`
 * is `[MinLength(1)]` and a null list crashes the backend. An exercise-less day never reaches here —
 * `phaseToInput` drops it — so by this point `day.exercises` is guaranteed non-empty.
 */
function dayToSessionInput(day: DayDraft, unidentified: UnidentifiedExercises): SessionInput {
  return {
    name: trimToNull(day.sessionName) ?? trimToNull(day.name) ?? FALLBACK_SESSION_NAME,
    order: 1,
    // Divergence 6. Emitted on every save because the save REPLACES the session row: omitting it is
    // not "leave the note alone", it is "delete the note". Nothing in this app authors one yet.
    notes: trimToNull(day.sessionNotes),
    estimatedDurationMinutes: day.durationMinutes,
    // The day's between-sets rest SEED. It resolves nothing on either side — every set already
    // carries its own concrete `prescribedRestTime` — so it only has to survive the round trip, which
    // is what lets the set grid derive each exercise's rest-column state by comparing against it.
    defaultRestTimeSeconds: day.restSeconds,
    exercises: day.exercises.map((exercise, index) => exerciseToInput(exercise, index, unidentified)),
  };
}

function dayToInput(day: DayDraft, index: number, unidentified: UnidentifiedExercises): DayInput {
  return {
    ...daySaveShape(day),
    order: index + 1,
    sessions: [dayToSessionInput(day, unidentified)],
  };
}

/**
 * A day's OWN members in the save request's shape: everything `dayToInput` emits except its position
 * and the session under it. One spelling of the day on the wire, shared by the save and by the AI's
 * fingerprint of a day (`aiFingerprint.ts`), so the two can never describe the same day differently.
 */
export function daySaveShape(day: DayDraft): Omit<DayInput, 'order' | 'sessions'> {
  return {
    slotToken: day.slotToken,
    // Both are carried straight off the draft, and the DRAFT is where the mode rule lives: a
    // self-paced day is minted with no weekday (`createDay`) and every operation that would place
    // one returns early off the mode, so nothing can put a weekday on one. The backend refuses one
    // outright with `plan_self_paced_day_has_weekday` (R2), so a second guard here would be a second
    // owner of a rule this tree already cannot break.
    dayOfWeek: day.dayOfWeek,
    // Advisory recovery only (R2), on both modes. Nothing sums it, and no date is derived from it.
    restDaysAfter: day.restDaysAfter,
    // The day's AUTHORED cycle lifespan, all three parts, on every save. This is the field set that
    // makes a plan freeform (training-plan R19), and every one of them fails OPEN rather than shut:
    // an omitted `activationCycle` or `retirementCycle` is read as "the whole phase", and an omitted
    // `excludedCycles` un-holes the day. So a save that forgot them would not lose the trainer's
    // window, it would mint their client the workouts the trainer had removed. `activationCycle` on
    // a DAY is authorable, unlike the exercise's — the two are different questions and the contract
    // treats them differently.
    activationCycle: day.activationCycle,
    retirementCycle: day.retirementCycle,
    excludedCycles: [...day.excludedCycles],
    name: trimToNull(day.name),
    // `serverId`, `serverOrder`, `serverCycles` and `liveClosedCycles` are DELIBERATELY absent. The
    // first two are load-time provenance the draft captures for the sequence-recalc preview; the
    // last two are the server's own resolution and its own record of what the client has lived.
    // Emitting any of them would hand the backend a position, a row id or its own answer as an edit
    // instruction, which is the shape ADR §2 records as having corrupted plans.
  };
}

/**
 * Does this phase carry a day the serialiser will emit?
 *
 * The ONE place "a day with no exercise is not a day" is written. `phaseToInput` drops such days and
 * refuses a phase left with none; `firstEmptyPhaseName` asks the same question ahead of the press.
 * Two spellings of it would drift the moment either grew a condition, and the failure mode would be
 * a Save button that looks live and answers with a refusal.
 */
function hasStockedDay(phase: PhaseDraft): boolean {
  return phase.days.some((day) => day.exercises.length > 0);
}

/**
 * The name of the first phase that cannot be serialised, or `null` when the tree is savable.
 *
 * **The Save button's gate, and it exists because of what a brand-new plan is.** The create modal
 * has no exercise picker, so a fresh draft is one phase with one empty day — and
 * `POST /plan-template` refuses that with a 400 whose message the trainer never sees. Asking here
 * lets the button explain itself instead: it stays focusable, carries the reason, and names the
 * phase, which is the only useful fact when a twelve-phase plan has one empty block in the middle.
 *
 * It does NOT replace `phaseToInput`'s throw. A predicate consulted by a component is a gate a
 * second call site can forget; the serialiser is where the tree stops. This is the explanation, that
 * is the guarantee, and both read `hasStockedDay` so they cannot disagree about what "empty" means.
 */
export function firstEmptyPhaseName(draft: PlanDraft): string | null {
  const phase = draft.phases.find((candidate) => !hasStockedDay(candidate));
  return phase === undefined ? null : phase.name.trim();
}

/**
 * What a request does with a part that holds nothing yet — a day with no exercise, a phase with no
 * such day. A save drops the day and refuses the phase (see `DraftSerialisationError`); the plan a
 * message to Buddy carries keeps both as they are (ai-plan-builder R13: an unfinished draft travels,
 * owner, 5 October 2026), since Buddy is asked about the plan the trainer has, and saving is not.
 */
type EmptyParts = 'refuse' | 'keep';

function phaseToInput(phase: PhaseDraft, index: number, unidentified: UnidentifiedExercises, empty: EmptyParts = 'refuse'): PhaseInput {
  const days = phase.days
    .filter((day) => empty === 'keep' || day.exercises.length > 0)
    .map((day, dayIndex) => dayToInput(day, dayIndex, unidentified));
  // Refuse rather than emit an absent collection — see `DraftSerialisationError`.
  if (days.length === 0 && empty === 'refuse') {
    throw new DraftSerialisationError(
      'emptyPhase',
      phase.name.trim(),
      `Phase "${phase.name.trim()}" has no day carrying an exercise, so it cannot be serialised: ` +
        `the wire requires at least one day per phase, and omitting the collection would silently ` +
        `save the phase as it was before it was emptied.`,
    );
  }
  // Contiguous 1..N by construction. Phase order is the ONE level where that is required rather
  // than merely convenient: `plan_phase_orders_not_contiguous` refuses a gap, because a phase's
  // order is judged against the consumed-prefix boundary and a gap would let it drift from the
  // phase's position, leaving the guard comparing two different numbers.
  return { ...phaseSaveShape(phase), order: index + 1, days };
}

/**
 * A phase's OWN members in the save request's shape: everything `phaseToInput` emits except its
 * position and its days. Shared with the AI's fingerprint of a phase, for the reason `daySaveShape`
 * is.
 */
export function phaseSaveShape(phase: PhaseDraft): Omit<PhaseInput, 'order' | 'days'> {
  return {
    // A phase's identity across a save, and the counterpart of the slot tokens days and exercises
    // carry. A plain server id rather than a minted token because a phase, unlike a day, only ever
    // enters the draft FROM the server. The backend needs it to tell two phases of the same type and
    // length apart when judging the consumed prefix.
    //
    // SPREAD rather than `id: … ?? undefined`, so a phase with no server row emits no `id` KEY at
    // all. The two serialise identically over JSON, but "absent" is the honest shape for something
    // that does not exist, and it keeps the rule readable: nothing else in this tree grows a row id.
    ...(phase.serverId === null ? {} : { id: phase.serverId }),
    name: phase.name.trim(),
    phaseType: phaseTypeToApi(phase.type),
    cycleCount: phase.cycles,
  };
}

/** The whole draft → the shared nested phase tree. Identical for POST and PATCH. */
function draftPhases(draft: PlanDraft, unidentified: UnidentifiedExercises = 'refuse', empty: EmptyParts = 'refuse'): PhaseInput[] {
  return draft.phases.map((phase, index) => phaseToInput(phase, index, unidentified, empty));
}

/** Create a brand-new template. Every required top-level field is present. */
export function draftToPostRequest(draft: PlanDraft): PostRequest {
  return createRequest(draft, 'refuse');
}

function createRequest(draft: PlanDraft, unidentified: UnidentifiedExercises, empty: EmptyParts = 'refuse'): PostRequest {
  return {
    name: draft.name.trim(),
    description: trimToNull(draft.description),
    category: draft.category ?? FALLBACK_CATEGORY,
    schedulingMode: schedulingModeToApi(draft.mode),
    // The cover's row id in the backend's pool. `coverImageUrl` is NOT emitted — the backend derives
    // it from this id, so sending it back would be sending the server its own answer.
    coverId: draft.coverId,
    phases: draftPhases(draft, unidentified, empty),
  };
}

/**
 * The trainer's edit of Buddy's draft: the create request, with one difference. A slot whose
 * exercise the trainer has not decided yet keeps `exerciseId: 0`, as the draft-edit route expects
 * (`PUT /ai/conversations/{id}/turns/{turnId}/draft`); every other refusal of `draftToPostRequest`
 * stands.
 */
export function draftToDraftEditRequest(draft: PlanDraft): PostRequest {
  return createRequest(draft, 'keep');
}

/**
 * The plan a message to Buddy carries (ai-plan-builder R13): the draft-edit request, with the
 * unfinished parts kept — a day with no exercise, a phase with no such day. The backend reads an
 * unfinished draft; only a save refuses one. Never sent to a plan route.
 */
export function draftToSnapshotRequest(draft: PlanDraft): PostRequest {
  return createRequest(draft, 'keep', 'keep');
}

/** One day as `draftToSnapshotRequest` writes it, everything inside it included (its position aside). */
export function daySnapshotShape(day: DayDraft): DayInput {
  return dayToInput(day, 0, 'keep');
}

/** One phase as `draftToSnapshotRequest` writes it, its days and everything in them included (its position aside). */
export function phaseSnapshotShape(phase: PhaseDraft): PhaseInput {
  return phaseToInput(phase, 0, 'keep', 'keep');
}

/**
 * Update an existing template — the SAME full tree as POST. A running plan's fork PATCHes exactly
 * like a library template: the backend regenerates the client's not-yet-started workouts from this
 * tree, and started or finished workouts are independent frozen snapshots it never touches.
 *
 * One field exists only here. `baseVersion` is the opaque optimistic-concurrency token, echoed
 * verbatim from the loaded `version` so a lost race is a 409 rather than last-write-wins; it is
 * omitted when the loaded template carried none, because sending a blank one would ask the backend
 * to compare against nothing.
 */
export function draftToPatchRequest(draft: PlanDraft): PatchRequest {
  return {
    name: draft.name.trim(),
    description: trimToNull(draft.description),
    // Divergence 3: omitted rather than defaulted, because PATCH's top-level fields are
    // independently optional and "absent" means "leave it alone".
    ...(draft.category === null ? {} : { category: draft.category }),
    schedulingMode: schedulingModeToApi(draft.mode),
    coverId: draft.coverId,
    phases: draftPhases(draft),
    baseVersion: draft.live.baseVersion?.trim() ? draft.live.baseVersion : undefined,
  };
}
